import linkedin from "../../images/linkedin-logo.png";
import React, { useState, useEffect } from "react";
import axios from "axios";
import Loading from "../../images/loading-black.svg"; // Ensure you have the loading.svg in the correct path
import cv from "../../images/cv-icon.png";
import github from "../../images/github.png";
import Office from "../../images/office-icon.png"; // Ensure you have the loading.svg in the correct path
import Languages from "../../images/languages-icon.png"; // Ensure you have the loading.svg in the correct path
import VideoIntro from "../../images/video-icon-green.png";
import jumpstart from "../../images/jumpstart_logo.jpeg";

import "flowbite";

// Add the formatDuration function at the top level
const formatDuration = (duration) => {
  if (!duration) return "";
  
  const years = parseInt(duration.years) || 0;
  const months = parseInt(duration.months) || 0;
  
  const yearText = years === 1 ? "yr" : "yrs";
  const monthText = months === 1 ? "mo" : "mos";
  
  if (years && months) {
    return `${years} ${yearText}, ${months} ${monthText}`;
  } else if (years) {
    return `${years} ${yearText}`;
  } else if (months) {
    return `${months} ${monthText}`;
  }
  return "";
};

const languageLogos = {
  JavaScript: "/images/coding_logo/javascript.png",
  Python: "/images/coding_logo/python.png",
  C: "/images/coding_logo/c.png",
  Swift: "/images/coding_logo/swift.png",
  "C#": "/images/coding_logo/csharp.png",
  "C++": "/images/coding_logo/cplusplus.png", // For C++
  Java: "/images/coding_logo/java.svg",
  Go: "/images/coding_logo/go.png",
  PHP: "/images/coding_logo/php.svg",
  Ruby: "/images/coding_logo/ruby.webp",
  Kotlin: "/images/coding_logo/kotlin.png",
  Rust: "/images/coding_logo/rust.png",

  // Newly added languages
  Clojure: "/images/coding_logo/clojure.png",
  Haskell: "/images/coding_logo/haskell.png",
  Dart: "/images/coding_logo/dart.png",
  Elixir: "/images/coding_logo/elixir.webp",
  "Objective-C": "/images/coding_logo/objc.svg",
  Perl: "/images/coding_logo/perl.png",
  Scala: "/images/coding_logo/scala.png",
  Typescript: "/images/coding_logo/typescript.png",
  React: "/images/coding_logo/react.png",
  Vue: "/images/coding_logo/vue.png",
  Angular: "/images/coding_logo/angular.png",
  "Next.js": "/images/coding_logo/next-js.svg",
};

// Converts URLs in plain text to clickable links, keeping other text intact
const linkifyText = (inputText) => {
  if (!inputText) return null;
  const text = Array.isArray(inputText) ? inputText.join(" ") : String(inputText);
  const urlRegex = /((https?:\/\/|www\.)[^\s]+)/gi;
  const parts = text.split(urlRegex);
  return parts.map((part, index) => {
    if (/^(https?:\/\/|www\.)/i.test(part)) {
      const href = part.startsWith("http") ? part : `https://${part}`;
      return (
        <a
          key={index}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline break-all"
        >
          {part}
        </a>
      );
    }
    return <span key={index}>{part}</span>;
  });
};

/**
 * Returns a custom sponsorship/visa message depending on the "RTW doc" value.
 */
function getVisaMessage(visaStatus, fullName = "", undergradGradYear = null, postgradGradYear = null) {
  const firstName = fullName.split(" ")[0]; // e.g. "Jane Doe" -> "Jane"
  const jumpstartEmail = "operations@jumpstart-uk.com";
  const contactTeamMsg = `Contact the Jumpstart team if you have any concerns.`;

  // Handle array format - extract the first element if it's an array
  let status = visaStatus;
  if (Array.isArray(visaStatus)) {
    status = visaStatus.length > 0 ? visaStatus[0] : null;
  }

  // Return empty string if no status
  if (!status) {
    return "";
  }

  switch (status) {
    case "Yes - Post Study Visa":
      const uGradYear = undergradGradYear ? parseInt(undergradGradYear, 10) : null;
      const pGradYear = postgradGradYear ? parseInt(postgradGradYear, 10) : null;
      
      // If EITHER graduation year is 2024 or later, the candidate is recent, so we don't show the warning.
      if ((uGradYear && uGradYear >= 2024) || (pGradYear && pGradYear >= 2024)) {
        return ""; // Don't show for 2024+ grads with PSW
      }
      return `${firstName} has a two-year post-study visa, so may require sponsorship in future. ${contactTeamMsg}`;
    case "No - I require Visa sponsorship to work in the UK":
      return `${firstName} needs visa sponsorship to work in the UK. ${contactTeamMsg}`;
    case "Yes - High Potential Individual Visa":
      return `${firstName} has a two-year high performance individual visa, so may require sponsorship in future. ${contactTeamMsg}`;
    case "Yes - Skilled Worker Visa":
    case "No - Requires Visa sponsorship":
      return `${firstName} requires visa sponsorship to work in the UK. Contact the Jumpstart team for future details.`;
    case "Other":
      return `${firstName} may require visa sponsorship, please confirm directly with the Jumpstart team if concerned.`;
    case "No - I don't have the right to work in the UK":
      return `${firstName} does not have the right to work in the UK and requires visa sponsorship. ${contactTeamMsg}`;
    case "No - Looking for Visa sponsorship, but willing to self-sponsor if necessary":
      return `${firstName} is looking for visa sponsorship but willing to self-sponsor if necessary. ${contactTeamMsg}`;
    case "No - Requires visa sponsorship - is well versed in the process + has a lawyer at hand if needed":
      return `${firstName} requires visa sponsorship but is well versed in the process. ${contactTeamMsg}`;
    default:
      // If British or Irish passport, show nothing:
      if (
        status === "Yes - British Passport" ||
        status === "Yes - Irish Passport" ||
        status === "Yes - Settlement Status" ||
        status === "Yes - British National (Overseas) Visa" ||
        status === "Yes - Settlement Status / Indefinite Leave to Remain" ||
        status === "Yes - Dependent Visa (e.g., Spouse/Partner Visa)" ||
        status === "Yes - Ancestry Visa"
      ) {
        return "";
      }
      // Fallback for anything else:
      if (status) {
        const cleanedStatus = status.replace("Yes - ", "");
        return `${firstName} has ${cleanedStatus} and may require visa sponsorship in the future. ${contactTeamMsg}`;
      }
      return "";
  }
}

function CandidateCard({
  candidate,
  setInterviewNeedUpdate,
  setShowPopup,
  setShowFinalPopup,
  candidatesRequest,
  setCandidatesRequest,
}) {
  const [decision, setDecision] = useState(null);
  const [companyLink, setCompanyLink] = useState("");
  const [showCopiedText, setShowCopiedText] = useState(false);
  const [svgMovedUp, setSvgMovedUp] = useState(false);
  const [currentUser, setCurrentUser] = useState(null); // Add current user state

  // Fetch current user information
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          console.log("No token found in localStorage");
          return;
        }
        
        console.log("Fetching current user...");
        const { data } = await axios.get("/api/auth/me", {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log("Current user fetched:", data.user);
        setCurrentUser(data.user);
      } catch (err) {
        console.error("Failed to fetch current user", err);
      }
    };

    fetchCurrentUser();
  }, []);

  useEffect(() => {
    const linkFromStorage = localStorage.getItem("companyLink");
    setCompanyLink(linkFromStorage);
  }, []);

  const handleSvgClick = () => {
    setSvgMovedUp(true);
    setShowCopiedText(true);
    setTimeout(() => {
      setSvgMovedUp(false);
      setShowCopiedText(false);
    }, 2000);
  };

  const copyToClipboard = (e) => {
    e.stopPropagation(); // Prevent event from bubbling up to parent elements
    let baseLink = companyLink || window.location.origin;
    try {
      const urlObj = new URL(baseLink);
      baseLink = urlObj.origin;
    } catch (err) {
      baseLink = window.location.origin;
    }
    const link = `${baseLink}/candidate/${candidate.url_id}`;
    navigator.clipboard.writeText(link).then(
      () => {
        handleSvgClick();
      },
      (err) => {
        console.error("Could not copy text: ", err);
      }
    );
  };

  // Define the color classes based on the stage
  const getColorClasses = (stream) => {
    switch (stream) {
      case "Generalist":
        return {
          bgColor: "bg-emerald-100",
          textColor: "text-emerald-700",
          workBgColor: "bg-emerald-50",
          fillColor: "fill-emerald-700",
          bgColor2: "hover:bg-emerald-400",
          borderColor: "border-emerald-400",
        };
      case "Sales":
        return {
          bgColor: "bg-blue-100",
          textColor: "text-blue-700",
          workBgColor: "bg-blue-50",
          fillColor: "fill-blue-700",
          bgColor2: "hover:bg-blue-400",
          borderColor: "border-blue-400",
        };
      case "Software Engineering":
        return {
          bgColor: "bg-orange-100",
          textColor: "text-orange-700",
          workBgColor: "bg-orange-50",
          fillColor: "fill-orange-700",
          bgColor2: "hover:bg-orange-400",
          borderColor: "border-orange-400",
        };
      case "Marketing":
        return {
          bgColor: "bg-purple-100",
          textColor: "text-purple-700",
          workBgColor: "bg-purple-50",
          fillColor: "fill-purple-700",
          bgColor2: "hover:bg-purple-400",
          borderColor: "border-purple-400",
        };
      default:
        return {
          bgColor: "bg-gray-100",
          textColor: "text-gray-700",
          workBgColor: "bg-gray-50",
          fillColor: "fill-gray-700",
          bgColor2: "hover:bg-gray-400",
          borderColor: "border-gray-400",
        };
    }
  };

  const { bgColor, textColor, workBgColor, fillColor, bgColor2, borderColor } =
    getColorClasses(candidate.stream);

  const [active, setActive] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false); // State to track if video is playing
  const [isVideoLoading, setIsVideoLoading] = useState(false); // State to track video loading
  const [showVisaTooltip, setShowVisaTooltip] = useState(false);

  // Function to calculate time since application
  const timeSince = (date) => {
    if (!date) return "";
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    let interval = seconds / 31536000;
    if (interval >= 1) {
      const numYears = Math.floor(interval);
      return numYears + (numYears === 1 ? " year ago" : " years ago");
    }
    interval = seconds / 2592000;
    if (interval >= 1) {
      const numMonths = Math.floor(interval);
      return numMonths + (numMonths === 1 ? " month ago" : " months ago");
    }
    interval = seconds / 86400;
    if (interval >= 1) {
      const numDays = Math.floor(interval);
      return numDays + (numDays === 1 ? " day ago" : " days ago");
    }
    interval = seconds / 3600;
    if (interval >= 1) {
      const numHours = Math.floor(interval);
      return numHours + (numHours === 1 ? " hour ago" : " hours ago");
    }
    interval = seconds / 60;
    if (interval >= 1) {
      const numMinutes = Math.floor(interval);
      return numMinutes + (numMinutes === 1 ? " minute ago" : " minutes ago");
    }
    const numSeconds = Math.floor(seconds);
    return numSeconds + (numSeconds === 1 ? " second ago" : " seconds ago");
  };

  // Helper function to get the embedded video URL
  const getEmbeddedVideoUrl = (url) => {
    if (!url) return null;
    
    // Handle Loom videos
    if (url.includes('loom.com')) {
      // Convert from https://www.loom.com/share/[id] to https://www.loom.com/embed/[id]
      return url.replace('/share/', '/embed/');
    }
    
    // Handle Vimeo videos
    if (url.includes('vimeo.com')) {
      // Extract the Vimeo ID
      const vimeoId = url.match(/vimeo\.com\/(\d+)/);
      if (vimeoId && vimeoId[1]) {
        return `https://player.vimeo.com/video/${vimeoId[1]}`;
      }
    }
    
    // Handle YouTube videos
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      // Extract YouTube ID
      let youtubeId = '';
      if (url.includes('youtube.com/watch')) {
        const searchParams = new URL(url).searchParams;
        youtubeId = searchParams.get('v');
      } else if (url.includes('youtu.be/')) {
        youtubeId = url.split('youtu.be/')[1];
      }
      
      if (youtubeId) {
        return `https://www.youtube.com/embed/${youtubeId}`;
      }
    }
    
    // Return original URL if we can't transform it
    return url;
  };

  // Toggle video playback
  const toggleVideo = (e) => {
    e.stopPropagation(); // Prevent card click navigation
    e.preventDefault(); // Prevent default behavior
    if (!isVideoPlaying) {
      setIsVideoLoading(true); // Set loading state when starting to play
    }
    setIsVideoPlaying(!isVideoPlaying);
  };
  
  // Handle video loaded
  const handleVideoLoaded = () => {
    setIsVideoLoading(false);
  };

  // Helper function to check if the video URL is valid
  const hasValidVideo = (url) => {
    return url && typeof url === 'string' && url.trim() !== '';
  };

  const handleUndo = async () => {
    const originalStage = "Application pending";
    const updatedData = {
      fields: {
        Stage: originalStage,
        "Candidate Interview Stage": originalStage,
        // Clear the tracking fields when undoing
        "Accepted By": null,
        "Rejected By": null,
      },
      interviewId: candidate.interviewId,
    };
    setError("");
    setLoading(true);
    try {
      await axios.patch(
        `/api/interviews/updateInterviewRequest`,
        updatedData
      );

      if (decision === "accepted") {
      }
      setDecision(null);

      setCandidatesRequest((prev) => prev + 1);
      
      console.log("Application status reset - user tracking fields cleared");
    } catch (err) {
      setError("Undo failed. Please reload the page.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (stage) => {
    const updatedData = {
      fields: {
        Stage: stage,
        "Candidate Interview Stage": stage,
      },
      interviewId: candidate.interviewId,
    };

    // Add user tracking fields based on the action
    if (currentUser && currentUser.id && !currentUser.isLegacyLogin) {
      if (stage === "Interviewing") {
        updatedData.fields["Accepted By"] = [currentUser.id];
        console.log(`Application accepted by user: ${currentUser.firstName || currentUser.email} (ID: ${currentUser.id})`);
      } else if (stage === "Rejected @ CV") {
        updatedData.fields["Rejected By"] = [currentUser.id];
        console.log(`Application rejected by user: ${currentUser.firstName || currentUser.email} (ID: ${currentUser.id})`);
      }
    } else if (currentUser && currentUser.isLegacyLogin) {
      console.log("Action by legacy/internal user - not tracking individual user");
    } else {
      console.log("No current user found - action will not be tracked to specific user");
    }

    setError(""); // Reset error message
    setLoading(true); // Set loading state to true
    try {
      await axios.patch(
        `/api/interviews/updateInterviewRequest`,
        updatedData
      );

      if (stage === "Interviewing") {
        setDecision("accepted");
      } else {
        setDecision("rejected");
      }

      setCandidatesRequest((prev) => {
        const updatedCount = prev - 1;
        if (updatedCount === 0) {
          setShowFinalPopup(true);
        }
        return updatedCount;
      });
    } catch (err) {
      setError(err.message); // Set error message
    } finally {
      setLoading(false); // Set loading state to false
    }
  };

  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const previewText =
    candidate.rationale?.split(" ").slice(0, 10).join(" ") + "...";

  const fallbackImageUrl =
    "https://cdn-icons-png.flaticon.com/512/4120/4120345.png";

  const workingStyleMapping = Array.isArray(candidate.working_style)
    ? candidate.working_style.map((style) => {
        if (style === "5 days a week in office") {
          return "Fully in office";
        } else if (style === "Hybrid (1-4 days in office)") {
          return "Hybrid";
        } else if (style === "Fully remote") {
          return "Fully remote";
        }
        return style;
      })
    : [];

  // Simple check: is this candidate's record ID in the recommended candidates list?
  const isRecommended = candidate.isRecommended;
  
  // Debug logging
  console.log(`Candidate: ${candidate.name}, Record ID: ${candidate.candidate_record_id}, URL ID: ${candidate.url_id}, Is recommended: ${isRecommended}`);

      return (
      <div className={`relative max-w-xl mx-auto mt-5 pt-4 pr-4 pl-4 bg-white rounded-lg overflow-hidden flex flex-col h-full transition-all duration-400 ${
        isRecommended 
          ? 'shadow-[0_4px_20px_rgba(2,180,145,0.15)] border border-emerald-100 hover:shadow-[0_8px_30px_rgba(2,180,145,0.25)] hover:border-emerald-200' 
          : 'hover:drop-shadow-lg'
      }`}>
      <a>
        <div className="relative">
          {/* Video or Image Container */}
          <div className="relative w-full h-72 rounded-lg overflow-hidden">
            {isVideoPlaying && hasValidVideo(candidate.video) ? (
              <div 
                className="video-player-container w-full h-full relative"
                onClick={(e) => e.stopPropagation()}
              >
                {isVideoLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10">
                    <svg className="animate-spin h-10 w-10 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                )}
                <iframe
                  src={getEmbeddedVideoUrl(candidate.video)}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title={`${candidate.name} video introduction`}
                  onLoad={handleVideoLoaded}
                ></iframe>
                {/* Close button */}
                <button 
                  className="absolute top-3 right-3 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-1 z-10"
                  onClick={toggleVideo}
                >
                  <svg 
                    className="h-6 w-6 text-white" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            ) : (
              <>
                <img
                  className="w-full h-72 object-cover rounded-lg"
                  src={candidate.image || fallbackImageUrl}
                  alt="Candidate"
                  onError={(e) => {
                    e.target.src = fallbackImageUrl;
                  }}
                />
                {/* Play button overlay - only show if candidate has a valid video */}
                {hasValidVideo(candidate.video) && (
                  <div 
                    className="absolute inset-0 flex items-center justify-center cursor-pointer"
                    onClick={toggleVideo}
                  >
                    <div className="relative bg-black bg-opacity-30 hover:bg-opacity-50 rounded-full p-5 transition-all duration-300 transform hover:scale-110">
                      <svg 
                        className="h-14 w-14 text-white" 
                        viewBox="0 0 24 24" 
                        fill="currentColor"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          
          {/* Recommended for you tag */}
          {isRecommended && (
            <span
              className="text-emerald-800 absolute top-2 left-2 inline-flex items-center gap-x-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 shadow-[0_2px_8px_rgba(16,185,129,0.25)] hover:shadow-[0_4px_12px_rgba(16,185,129,0.35)] transition-all duration-200"
            >
              <img
                src={jumpstart}
                title="Jumpstart icon"
                className="h-4 w-4 rounded-sm"
              />
              Recommended for you
            </span>
          )}
          
        </div>
        
        {/* Application status tag - positioned on the card container, not the image */}
        <span
          className={`absolute -top-3 right-2 inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium ${bgColor} ${textColor} shadow-md transform translate-y-1/2`}
        >
          {candidate.stage === "Application pending" ? (
            <>
              Applied to <strong>{candidate.role_title}</strong>
            </>
          ) : (
            candidate.stage
          )}
        </span>
      </a>
      <div className="pt-2 flex flex-col flex-grow">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <h5 className="text-xl font-semibold tracking-tight text-gray-900">
              {candidate.name}
            </h5>
            {/* Copy link icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className={`w-6 h-6 ml-1 cursor-pointer transform flex-shrink-0 ${
                svgMovedUp ? "hidden" : ""
              }`}
              onClick={copyToClipboard}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.5 8.25V6a2.25 2.25 0 00-2.25-2.25H6A2.25 2.25 0 003.75 6v8.25A2.25 2.25 0 006 16.5h2.25m8.25-8.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-7.5A2.25 2.25 0 018.25 18v-1.5m8.25-8.25h-6a2.25 2.25 0 00-2.25 2.25v6"
              />
            </svg>

            {showCopiedText && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-6 h-6 ml-1 flex-shrink-0"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
            )}
            <img
              src={linkedin}
              alt={candidate.name}
              title="Linkedin icon"
              className="h-6 w-6 ml-2 rounded-sm flex-shrink-0"
              onClick={(e) => {
                e.preventDefault();
                let url = candidate.linkedin;
                if (!url.startsWith("https://")) {
                  url = "https://" + url;
                }
                window.open(url, "_blank");
              }}
              style={{ cursor: "pointer" }}
            />

            {candidate.cv && (
              <img
                src={cv}
                alt={candidate.cv}
                title="CV icon"
                className="h-6 w-6 ml-2 flex-shrink-0"
                onClick={(e) => {
                  e.preventDefault();
                  window.open(candidate.cv, "_blank");
                }}
                style={{ cursor: "pointer" }}
              />
            )}
            {/* Video Intro icon removed */}

            {candidate.github && (
              <img
                src={github}
                alt={candidate.github}
                title="Github icon"
                className="h-6 w-6 ml-2 flex-shrink-0"
                onClick={(e) => {
                  e.preventDefault();
                  window.open(candidate.github, "_blank");
                }}
                style={{ cursor: "pointer" }}
              />
            )}
            {candidate.portfolio && (
              <svg
                onClick={(e) => {
                  e.preventDefault();
                  window.open(candidate.portfolio, "_blank");
                }}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                className="w-6 h-6 cursor-pointer ml-2 flex-shrink-0"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"
                />
              </svg>
            )}
            {candidate.created && (
              <span className="ml-2 inline-flex flex-shrink-0 items-center gap-x-1.5 rounded-md bg-yellow-100 px-1.5 py-0.5 text-xs font-medium text-yellow-800">
                <svg className="h-1.5 w-1.5 fill-yellow-500" viewBox="0 0 6 6" aria-hidden="true"><circle cx="3" cy="3" r="3"></circle></svg>
                Applied {timeSince(candidate.created)}
              </span>
            )}
          </div>
          <div className="flex flex-col text-right text-sm">
            Expected Salary Range:
            {candidate.minimum_salary && (
              <h5 className="text-sm font-semibold tracking-tight text-gray-900">
                {candidate.minimum_salary} - {candidate.ideal_salary}
                {candidate.commission_expectation &&
                  ` + ${candidate.commission_expectation} commission`}
              </h5>
            )}
          </div>
        </div>

        <div className="mt-2">
          {candidate.roles &&
            candidate.roles.map((role, index) => (
              <span
                key={index}
                className={`inline-flex mr-2 mb-2 items-center gap-x-1.5 rounded-md px-2 py-1 text-sm md:text-sm font-medium ${bgColor} ${textColor}`}
              >
                {role}
              </span>
            ))}
          {candidate.sales && (
            <span
              className={`inline-flex mr-2 mb-2 items-center gap-x-1.5 rounded-md px-2 py-1 text-sm md:text-md font-medium ${bgColor} ${textColor}`}
            >
              {candidate.sales}
            </span>
          )}
          {candidate.swe_roles &&
            candidate.swe_roles.map((role, index) => (
              <span
                key={index}
                className={`inline-flex mr-2 mb-2 items-center gap-x-1.5 rounded-md px-2 py-1 text-sm md:text-sm font-medium ${bgColor} ${textColor}`}
              >
                {role}
              </span>
            ))}
          {candidate.exp_swe && (
            <span
              className={`inline-flex mr-2 mb-2 items-center gap-x-1.5 rounded-md px-2 py-1 text-sm md:text-md font-medium ${bgColor} ${textColor}`}
            >
              {candidate.exp_swe}
            </span>
          )}
          {candidate.time_coding && (
            <span
              className={`inline-flex mr-2 mb-2 items-center gap-x-1.5 rounded-md px-2 py-1 text-sm md:text-md font-medium ${bgColor} ${textColor}`}
            >
              {candidate.time_coding} yrs of coding exp
            </span>
          )}
        </div>

        {/* Visa Status Component - Styled like Jumpstart POV */}
        {candidate.right_to_work && getVisaMessage(candidate.right_to_work, candidate.name, candidate.undergrad_graduation_year, candidate.postgrad_graduation_year) && (
          <div className="mb-4">
            <div 
              className={`rounded-lg overflow-hidden transition-all duration-300 ${
                showVisaTooltip 
                  ? 'shadow-md border border-gray-100' 
                  : 'border border-transparent hover:border-gray-100'
              }`}
              onClick={(e) => {
                e.stopPropagation(); // Prevent card navigation
                setShowVisaTooltip(!showVisaTooltip);
              }}
              onMouseEnter={() => setShowVisaTooltip(true)}
              onMouseLeave={() => setShowVisaTooltip(false)}
            >
              {/* Header */}
              <div 
                className={`flex items-center justify-between p-3 cursor-pointer ${
                  showVisaTooltip 
                    ? 'bg-gradient-to-r from-gray-50 to-gray-100/30' 
                    : 'bg-gray-50/60 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded bg-gray-100">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-gray-600">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm1.294 6.336a6.721 6.721 0 01-3.17.789 6.721 6.721 0 01-3.168-.789 3.376 3.376 0 016.338 0z" />
                    </svg>
                  </div>
                  <span className="font-medium text-sm text-gray-800">Visa Status</span>
                </div>
                <div className={`rounded-full p-1 ${showVisaTooltip ? 'bg-gray-100/80' : 'bg-transparent'}`}>
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    strokeWidth={2} 
                    stroke="currentColor" 
                    className={`w-4 h-4 text-gray-600 transition-transform duration-300 ${
                      showVisaTooltip ? 'rotate-180 transform' : ''
                    }`}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                  </svg>
                </div>
              </div>
              
              {/* Content - animate height */}
              <div 
                className={`overflow-hidden transition-all duration-300 ${
                  showVisaTooltip ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="p-3 pt-2 bg-white border-t border-gray-100">
                  <p 
                    className="text-sm text-gray-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: getVisaMessage(candidate.right_to_work, candidate.name, candidate.undergrad_graduation_year, candidate.postgrad_graduation_year) }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="inline-flex mr-2 mb-2 items-center gap-x-1.5 rounded-md text-black text-sm md:text-md font-medium flex gap-x-2">
          {candidate.coding_languages &&
            candidate.coding_languages.map((language, index) => (
              <img
                key={index}
                src={
                  languageLogos[language.trim()] || "path/to/default-logo.png"
                }
                alt={language}
                className="h-6 w-6"
              />
            ))}
        </div>

        <div className="flex pb-2 pt-1">
          {candidate.working_style &&
            Array.isArray(candidate.working_style) &&
            workingStyleMapping.length > 0 && (
              <p className="pr-5 font-normal flex text-sm text-black items-center">
                <img className="h-6 pr-1" src={Office} alt="Office icon" />
                {workingStyleMapping.join(" or ")}
              </p>
            )}
          {candidate.languages &&
            Array.isArray(candidate.languages) &&
            candidate.languages.some((language) => language !== "N/A") && (
              <p className="font-normal flex text-sm text-black items-center">
                <img className="h-6 pr-2" src={Languages} alt="Languages icon" />
                {candidate.languages
                  .filter((language) => language !== "N/A")
                  .join(", ")}
              </p>
            )}
        </div>

        {candidate.undergrad_degree_type && (
          <p className="text-sm">
            <span className="font-semibold">
              {candidate.undergrad_degree_type}{" "}
              {candidate.undergrad_degree_subject}
            </span>
            {candidate.undergrad_university && " @ "}
            {candidate.undergrad_university} ({candidate.undergrad_result}
            {candidate.undergrad_result &&
              candidate.undergrad_graduation_year &&
              " - "}
            {candidate.undergrad_graduation_year})
          </p>
        )}

        {candidate.postgrad_degree_type && (
          <p className="text-sm pt-2">
            <span className="font-semibold">
              {candidate.postgrad_degree_type}{" "}
              {candidate.postgrad_degree_subject}
            </span>
            {candidate.postgrad_university && " @ "}
            {candidate.postgrad_university} ({candidate.postgrad_result}
            {candidate.postgrad_result &&
              candidate.postgrad_graduation_year &&
              " - "}
            {candidate.postgrad_graduation_year})
          </p>
        )}

        <div className={`pb-2 mt-2 px-2 rounded-md ${workBgColor}`}>
          {candidate.recentTarget && (
            <div className="font-normal text-sm text-gray-700 pt-2">
              <span className="font-semibold text-black">
                Recent Target Achieved:
              </span>{" "}
              {candidate.recentTarget}
            </div>
          )}
          {candidate.work_experience_1_company && (
            <>
              <p className="text-sm pt-2">
                <span className="font-semibold">
                  {candidate.work_experience_1_role}
                </span>{" "}
                @ {candidate.work_experience_1_company} (
                {formatDuration(candidate.work_experience_1_duration)})
              </p>
              <p className="text-gray-500 text-sm">
                {candidate.work_experience_1_description}
              </p>
            </>
          )}
          {candidate.work_experience_2_company && (
            <>
              <p className="text-sm pt-2">
                <span className="font-semibold">
                  {candidate.work_experience_2_role}
                </span>{" "}
                @ {candidate.work_experience_2_company} (
                {formatDuration(candidate.work_experience_2_duration)})
              </p>
              <p className="text-gray-500 text-sm">
                {candidate.work_experience_2_description}
              </p>
            </>
          )}
        </div>

        {candidate.description && (
          <p className="mt-2 font-normal text-sm text-gray-700">
            <span className="font-semibold text-black">Most proud of: </span>
            {candidate.description}
          </p>
        )}

        {candidate.video && (
          <a
            onClick={(e) => {
              e.preventDefault();
              window.open(candidate.video, "_blank");
            }}
            className="mt-2 font-normal hidden text-sm text-blue-600 "
            style={{ cursor: "pointer" }}
          >
            <span className="font-semibold">Video intro - get to know me!</span>
          </a>
        )}

        {candidate.rationale && (
          <div className={`pb-2 mt-2 px-2 rounded-md ${workBgColor}`}>
            <div className="font-normal text-sm text-gray-700 pt-2">
              <span className="font-semibold text-black">
                Application Rationale:
              </span>{" "}
              {isExpanded ? linkifyText(candidate.rationale) : linkifyText(previewText)}
              <button
                onClick={handleToggle}
                className="text-blue-500 ml-2 underline"
              >
                {isExpanded ? "Show Less" : "Read More"}
              </button>
            </div>
          </div>
        )}

        {candidate.stage === "Application pending" && (
          <div className="flex mt-auto mb-4">
            {loading ? (
              <div className="flex justify-center items-center w-full h-12 bg-gray-100 rounded-lg">
                <img src={Loading} className="h-6" alt="Loading" />
              </div>
            ) : decision ? (
              <div className="w-full">
                {decision === "accepted" ? (
                  <div className="flex items-center justify-center w-full px-4 py-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600 mr-3" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <p className="text-sm font-medium text-green-800">
                        Accepted. You'll be introduced soon.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between w-full px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm font-medium text-gray-800">
                        Candidate has been rejected.
                      </p>
                    </div>
                    <button
                      onClick={handleUndo}
                      className="text-sm font-medium text-gray-600 hover:text-gray-900 hover:underline"
                    >
                      Undo
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="flex mt-auto mb-4 w-full">
                  <button
                    onClick={() => handleSubmit("Interviewing")}
                    className="w-full group px-8 py-2 text-sm mr-1 text-black items-center rounded-lg font-medium transform hover:bg-white bg-[#02B491] hover:text-black text-white transition duration-400 border-2 border-[#02B491] flex flex-col justify-center items-center"
                    title="We'll introduce you via email."
                  >
                    <span className="text-white group-hover:text-black">
                      Accept Interview Request
                    </span>
                    <p className="flex items-center justify-center mt-1 text-white font-normal text-xs group-hover:text-black">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="w-4 h-4 mr-2"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M11.25 11.25l.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"
                        />
                      </svg>
                      You'll be introduced over email!
                    </p>
                  </button>

                  <button
                    data-tooltip-target="tooltip-default"
                    type="button"
                    onClick={() => handleSubmit("Rejected @ CV")}
                    className="px-8 group py-2 text-sm ml-1 whitespace-nowrap rounded-lg font-medium transform bg-white hover:bg-gray-500 text-black hover:text-white transition duration-400 border-2 border-gray-500"
                  >
                    Not a match
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default CandidateCard;
